<template>
  <div v-show="show" class="member-indoor-item">
    <!-- 検索ツールバー -->
    <div class="tool-bar">
      <member-indoor-item-filter
        v-bind="filterCondition"
        v-on:condition-changed="filterCondition = $event"
      />
    </div>
    <div class="title-bar">
      <h1>{{ indoorItemCount }}</h1>
    </div>
    <div class="indoor-item-list-container">
      <loading-mask v-bind:isLoading="isLoading">
        <ul class="indoor-item-list">
          <member-indoor-item-site-node
            v-for="indoorSiteNode in indoorSiteNodes"
            v-bind:key="`indoor-site-${indoorSiteNode.id}`"
            v-bind="indoorSiteNode"
            v-bind:users="users"
            v-bind:selectedId="selectedId"
            v-on="customListeners"
          />
          <member-indoor-item-building-node
            v-for="indoorBuildingNode in parentIndoorBuildingNodes"
            v-bind:key="`indoor-building-${indoorBuildingNode.id}`"
            v-bind="indoorBuildingNode"
            v-bind:users="users"
            v-bind:selectedId="selectedId"
            v-on="customListeners"
          />
        </ul>
      </loading-mask>
    </div>
  </div>
</template>

<script>
import MemberIndoorItemFilter from '@/components/member/AppMemberIndoorItemFilter.vue'
import MemberIndoorItemSiteNode from '@/components/member/AppMemberIndoorItemSiteNode.vue'
import MemberIndoorItemBuildingNode from '@/components/member/AppMemberIndoorItemBuildingNode.vue'
import { useHandleApiError } from '@/composables/UseHandleApiError.js'
import ArrayUtil from '@/js/util/ArrayUtil'
import { mapGetters } from 'vuex'

/**
 * キーワードのどれかが含まれる場合はtrue
 * @param {String} text 検査対象文字列
 * @param {String|Array.<String>} keyword 検索文字配列
 * @returns {Boolean} キーワードのどれかが含まれる場合はtrue
 */
const isTextIncludsSomeKeyword = (text, keyword) => {
  const keywords = Array.isArray(keyword) ? keyword : [keyword]
  const regexes = keywords.map((k) => new RegExp(k))
  return regexes.some((regex) => regex.test(text))
}

/**
 * フィルタ結果を結合したID取得
 * 両方の集合があるときはAND 片方のみのときはOR
 * @param {Array.<Number>} idsA ID集合A
 * @param {Array.<Number>} idsB ID集合B
 * @returns {Array.<Number>} ID集合B
 */
const getFilterIds = (idsA, idsB) => {
  if (idsA && idsB) {
    return idsA.filter((id) => idsB.includes(id))
  } else if (idsA) {
    return idsA
  } else if (idsB) {
    return idsB
  } else {
    return null
  }
}

/**
 * 地図画面 - サイト・ビルタブ
 */
export default {
  name: 'AppMemberIndoorItem',
  components: {
    MemberIndoorItemFilter,
    MemberIndoorItemSiteNode,
    MemberIndoorItemBuildingNode,
  },
  inheritAttrs: false,
  props: {
    /**
     * 表示フラグ
     */
    show: {
      type: Boolean,
      default: false,
    },
    /**
     * ユーザー情報
     */
    users: {
      type: Array,
      defualt: () => [],
    },
    /**
     * プレイスデータ(屋内のみ)
     */
    indoorPlaces: {
      type: Array,
      default: () => [],
    },
    /**
     * 絞り込み条件
     */
    condition: {
      type: Object,
      default: () => {},
    },
    /**
     * ユーザー数表示フラグ
     */
    showUsersCount: {
      type: Boolean,
      default: true,
    },
  },
  setup(props, context) {
    const { handleApiError } = useHandleApiError(props, context)
    return { handleApiError }
  },
  data: function () {
    return {
      // 対象IDを絞るための条件
      filterCondition: {
        keyword: null,
        indoorSiteIds: null,
        indoorBuildingIds: null,
      },
      // 選択ID
      selectedId: {
        indoorSite: null,
        indoorBuilding: null,
        indoorLevel: null,
        indoorPlace: null,
      },
      // ロード中の場合はtrue
      isLoading: false,
    }
  },
  computed: {
    ...mapGetters('indoorSite', ['indoorSites']),
    ...mapGetters('indoorBuilding', ['indoorBuildings', 'indoorBuildingMap']),
    ...mapGetters('indoorLevel', ['indoorLevels']),
    /**
     * 「すべてのサイト・ビル」対象の場合はtrue
     * @type {boolean}
     */
    isAllSiteBuilding: function () {
      const { indoorSiteIds, indoorBuildingIds } = this.filterCondition
      return indoorSiteIds == null && indoorBuildingIds == null
    },
    /**
     * キーワード(分解)
     * @type {Array.<String>}
     */
    keywords: function () {
      if (!this.filterCondition.keyword) return null
      return this.filterCondition.keyword.replace(/　/g, ' ').split(' ')
    },
    /**
     * キーワードに該当する現場ID
     * 条件の指定が無い場合はnullとなる
     * @type {Array.<Number>}
     */
    indoorSiteIdsIncludesKeyword: function () {
      if (!this.indoorSites) return null
      if (!this.keywords) return null
      return this.indoorSites
        .filter((site) =>
          isTextIncludsSomeKeyword(site.site_name, this.keywords)
        )
        .map((site) => site.id)
    },
    /**
     * キーワードに該当する建物ID
     * 条件の指定が無い場合はnullとなる
     * @type {Array.<Number>}
     */
    indoorBuildingIdsIncludesKeyword: function () {
      if (!this.indoorBuildings) return null
      if (!this.keywords) return null
      return this.indoorBuildings
        .filter((building) =>
          [building.name, building.short_name].some((name) =>
            isTextIncludsSomeKeyword(name, this.keywords)
          )
        )
        .map((building) => building.id)
    },
    /**
     * キーワードに該当する屋内レベルID
     * 条件の指定が無い場合はnullとなる
     * @type {Array.<Number>}
     */
    indoorLevelIdsIncludesKeyword: function () {
      if (!this.indoorLevels) return null
      if (!this.keywords) return null
      return this.indoorLevels
        .filter((level) =>
          [level.name, level.short_name].some((name) =>
            isTextIncludsSomeKeyword(name, this.keywords)
          )
        )
        .map((level) => level.id)
    },
    /**
     * フィルタリングの結果対象となるID
     * 条件の指定が無い場合はnullとなる
     * @type {Object}
     */
    filterIds: function () {
      return {
        indoorSite: getFilterIds(
          this.indoorSiteIdsIncludesKeyword,
          this.filterCondition.indoorSiteIds
        ),
        indoorBuilding: getFilterIds(
          this.indoorBuildingIdsIncludesKeyword,
          this.filterCondition.indoorBuildingIds
        ),
        indoorLevel: getFilterIds(this.indoorLevelIdsIncludesKeyword, null),
      }
    },
    /**
     * ツリー表示用プレイスノードデータ
     * @type {Array.<Object>}
     */
    indoorPlaceNodes: function () {
      return this.indoorPlaces.map((place) => ({
        id: place.id,
        name: place.place_name,
        indoorLevelId: place.indoor_level_id,
        geometryType:
          (place.geom && place.geom.geometry && place.geom.geometry.type) ||
          null,
        showUsersCount: this.showUsersCount,
      }))
    },
    /**
     * ツリー表示用屋内レベルノードデータ
     * @type {Array.<Object>}
     */
    indoorLevelNodes: function () {
      return this.indoorLevels.map((level) => {
        const indoorPlaceNodes = this.indoorPlaceNodes.filter(
          (d) => d.indoorLevelId === level.id
        )
        const isTarget = this.filterIds.indoorLevel
          ? this.filterIds.indoorLevel.includes(level.id)
          : true

        return {
          id: level.id,
          name: level.name,
          indoorBuildingIds: level.indoor_buildings.map(
            (building) => building.id
          ),
          indoorPlaceNodes,
          isTarget,
          showUsersCount: this.showUsersCount,
        }
      })
    },
    /**
     * ツリー表示用建物ノードデータ
     * @type {Array.<Object>}
     */
    indoorBuildingNodes: function () {
      return this.indoorBuildings.map((building) => {
        const sortOrderMap = new Map(
          building.indoor_levels.map((level) => [level.id, level.sort_order])
        )
        const indoorLevelNodes = this.indoorLevelNodes
          .filter((d) => d.indoorBuildingIds.includes(building.id))
          .sort((a, b) => sortOrderMap.get(a.id) - sortOrderMap.get(b.id))
        let isTarget = this.filterIds.indoorBuilding
          ? this.filterIds.indoorBuilding.includes(building.id)
          : true
        // 自身が条件に含まれていない場合は子のisTargetで判断する
        if (!isTarget) {
          isTarget = this.filterIds.indoorLevel
            ? indoorLevelNodes.some((d) => d.isTarget)
            : false
        }

        return {
          id: building.id,
          name: building.name,
          indoorSiteIds: building.indoor_sites.map((site) => site.id),
          indoorLevelNodes,
          isTarget,
          showUsersCount: this.showUsersCount,
        }
      })
    },
    /**
     * ツリー表示用現場ノードデータ
     * @type {Array.<Object>}
     */
    indoorSiteNodes: function () {
      return this.indoorSites.map((site) => {
        const indoorBuildingNodes = this.indoorBuildingNodes.filter((d) =>
          d.indoorSiteIds.includes(site.id)
        )
        const isTarget = this.filterIds.indoorSite
          ? this.filterIds.indoorSite.includes(site.id)
          : true

        return {
          id: site.id,
          name: site.site_name,
          indoorBuildingNodes,
          isTarget,
          showUsersCount: this.showUsersCount,
        }
      })
    },
    /**
     * フィルタリングの結果リストに表示される対象ID
     * すべての場合は null になる
     * @type {Object}
     */
    targetIds: function () {
      const ids = {
        indoorSite: this.indoorSiteNodes
          .filter((n) => n.isTarget)
          .map((n) => n.id),
        indoorBuilding: this.indoorBuildingNodes
          .filter((n) => n.isTarget)
          .map((n) => n.id),
        indoorLevel: this.indoorLevelNodes
          .filter((n) => n.isTarget)
          .map((n) => n.id),
      }

      // サイトビル選択が「すべてのサイト・ビル」の場合
      if (this.isAllSiteBuilding) {
        const isAllSiteTarget =
          ids.indoorSite.length === this.indoorSiteNodes.length
        const isAllBuildingTarget =
          ids.indoorBuilding.length === this.indoorBuildingNodes.length
        // 結果的にもすべてのサイトとビルが選択されている場合は null として返却
        if (isAllSiteTarget && isAllBuildingTarget) {
          return {
            indoorSite: null,
            indoorBuilding: null,
            indoorLevel: null,
          }
        }
      }

      return ids
    },
    /**
     * 親になる建物ノードデータ
     * @type {Array.<Object>}
     */
    parentIndoorBuildingNodes: function () {
      return this.indoorBuildingNodes.filter(
        (node) => !node.indoorSiteIds.length
      )
    },
    /**
     * 親として取り扱える建物ノードデータ
     * parentIndoorBuildingNodes + 親サイトがtargetになっていない indoorBuildingNodes
     * @type {Object[]}
     */
    indoorBuildingNodesAsParent: function () {
      const buildingNodesOfNotTargetSiteMap = new Map()
      for (const site of this.indoorSiteNodes) {
        if (site.isTarget) continue
        for (const b of site.indoorBuildingNodes) {
          buildingNodesOfNotTargetSiteMap.set(b.id, b)
        }
      }
      return [
        ...this.parentIndoorBuildingNodes,
        ...buildingNodesOfNotTargetSiteMap.values(),
      ]
    },
    /**
     * 屋内アイテムの件数
     * @type {String}
     */
    indoorItemCount: function () {
      const sitesCount = this.indoorSiteNodes.filter(
        (s) => s.isTarget !== false
      ).length
      const countTargetBuildingNodes = [
        ...this.indoorSiteNodes
          .filter((s) => s.isTarget === false)
          .flatMap((s) => s.indoorBuildingNodes),
        ...this.parentIndoorBuildingNodes,
      ]
      const buildingCount = countTargetBuildingNodes.filter(
        (b) => b.isTarget !== false
      ).length
      return `${this.$tc(
        'appMemberIndoorItem.indoorSites',
        sitesCount
      )}${this.$tc('appMemberIndoorItem.separator')}${this.$tc(
        'appMemberIndoorItem.indoorBuildings',
        buildingCount
      )}`
    },
    /**
     * condition の値から反映すべき targetIds 値
     * @type {Object}
     */
    targetIdsFromCondition: function () {
      const {
        indoor: { indoorSiteIds, indoorBuildingIds },
      } = this.condition
      const isSiteNullish = indoorSiteIds == null
      const isBuildingNullish = indoorBuildingIds == null

      if (isSiteNullish === isBuildingNullish) {
        // nullish の状態がサイト・ビルともに同じの場合は調整不要
        return { indoorSite: indoorSiteIds, indoorBuilding: indoorBuildingIds }
      } else if (isSiteNullish) {
        // サイトだけ nullish の場合
        // サイトが全てでビルが特定のものだけという選択はありえない => サイトは無選択なことにする
        return {
          indoorSite: [],
          indoorBuilding: indoorBuildingIds,
        }
      } else {
        // ビルだけ nullish な場合
        // サイトの子ビルを選択されていることにする
        const childBuildingIds = indoorSiteIds.flatMap((sid) =>
          (
            this.indoorSiteNodes.find((s) => s.id === sid)
              ?.indoorBuildingNodes || []
          ).map((b) => b.id)
        )
        return {
          indoorSite: indoorSiteIds,
          indoorBuilding: [...new Set(childBuildingIds)].sort((a, b) => a - b),
        }
      }
    },
    /**
     * props.condition のサイト・ビルと targetIds のサイト・ビルの値が一致と見なせる場合はtrue
     * @type {Boolean}
     */
    isTargetSame: function () {
      const { indoorSite: cSiteIds, indoorBuilding: cBuildingIds } =
        this.targetIdsFromCondition
      const { indoorSite: tSiteIds, indoorBuilding: tBuildingIds } =
        this.targetIds

      return (
        ArrayUtil.isSame(cSiteIds, tSiteIds) &&
        ArrayUtil.isSame(cBuildingIds, tBuildingIds)
      )
    },
    /**
     * イベント発出用絞り込み条件
     * @type {Object}
     */
    conditionToEmit: function () {
      // サイト・ビルがすべて対象か？
      const isAllSiteBuildingTarget =
        this.targetIds.indoorSite == null &&
        this.targetIds.indoorBuilding == null

      // サイト決定
      const isSelectedBuildingParent = !!this.indoorBuildingNodesAsParent.find(
        (n) => n.id === this.selectedId.indoorBuilding
      )
      let indoorSiteIds
      if (isSelectedBuildingParent) {
        // 親ノードのビルが選択されている場合はサイトは無とする
        indoorSiteIds = []
      } else if (this.selectedId.indoorSite) {
        indoorSiteIds = [this.selectedId.indoorSite]
      } else if (isAllSiteBuildingTarget) {
        indoorSiteIds = null
      } else {
        indoorSiteIds = this.targetIds.indoorSite
      }

      // ビル決定
      let indoorBuildingIds
      if (this.selectedId.indoorBuilding) {
        indoorBuildingIds = [this.selectedId.indoorBuilding]
      } else if (this.selectedId.indoorSite) {
        const siteNode = this.indoorSiteNodes.find(
          (n) => n.id === this.selectedId.indoorSite
        )
        indoorBuildingIds = siteNode.indoorBuildingNodes.map((n) => n.id)
      } else if (isAllSiteBuildingTarget) {
        indoorBuildingIds = null
      } else {
        indoorBuildingIds =
          this.targetIds.indoorBuilding ||
          this.indoorBuildingNodes.map((n) => n.id)
      }

      return {
        indoor: {
          indoorSiteIds,
          indoorBuildingIds,
          indoorLevelId: this.selectedId.indoorLevel,
        },
        place: {
          // フロアが選択されていない場合は屋外なので元のconditionの値を引き継ぐ
          placeId: this.selectedId.indoorLevel
            ? this.selectedId.indoorPlace
            : this.condition.place.placeId,
        },
      }
    },
    /**
     * watcher 監視用 conditionToEmit
     * Objectを監視させると中身に変化のないshallow copyでも発火するのでJSON.stringifyで文字列化する
     * @type {String}
     */
    conditionToEmitForWatch: function () {
      return JSON.stringify(this.conditionToEmit)
    },
    /**
     * props.condition のサイト・ビルと conditionToEmit のサイト・ビルの値が一致と見なせる場合はtrue
     * @type {Boolean}
     */
    isIndoorConditionSame: function () {
      const {
        indoor: {
          indoorSiteIds: cSiteIds,
          indoorBuildingIds: cBuildingIds,
          indoorLevelId: cLevelId,
        },
      } = this.condition
      const {
        indoor: {
          indoorSiteIds: eSiteIds,
          indoorBuildingIds: eBuildingIds,
          indoorLevelId: eLevelId,
        },
      } = this.conditionToEmit

      return (
        ArrayUtil.isSame(cSiteIds, eSiteIds) &&
        ArrayUtil.isSame(cBuildingIds, eBuildingIds) &&
        cLevelId === eLevelId
      )
    },
    /**
     * イベントのカスタムリスナー
     * name-click は透過させ、indoor-item-selectedは独自リスナーを当てる
     * @type {Object}
     */
    customListeners: function () {
      return {
        ...this.$listeners,
        'indoor-item-selected': this.$_onItemSelected,
      }
    },
  },
  created: function () {
    this.$_showLoading()
    Promise.all([
      this.$_updateIndoorSite(),
      this.$_updateIndoorBuilding(),
      this.$_updateIndoorLevel(),
    ]).then(() => {
      this.$_applyCondition()
      this.$_hideLoading()
    })
  },
  watch: {
    condition: {
      deep: true,
      handler: function () {
        this.$_applyCondition()
      },
    },
    targetIds: function () {
      this.$_onTargetIdsChanged()
    },
    selectedId: function () {
      this.$_emitSelectedIdChangedEvent()
    },
    conditionToEmitForWatch: function () {
      this.$_emitConditionChangedEvent()
    },
  },
  methods: {
    /**
     * 現場情報更新
     */
    $_updateIndoorSite: async function () {
      try {
        await this.$store.dispatch('indoorSite/updateIndoorSitesWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorSites. use fetched before.')
        })
      }
    },

    /**
     * 屋内建物情報更新
     */
    $_updateIndoorBuilding: async function () {
      try {
        await this.$store.dispatch(
          'indoorBuilding/updateIndoorBuildingsWithThrottle'
        )
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorBuildings. use fetched before.')
        })
      }
    },

    /**
     * 屋内レベル情報更新
     */
    $_updateIndoorLevel: async function () {
      try {
        await this.$store.dispatch('indoorLevel/updateIndoorLevelsWithThrottle')
      } catch (error) {
        this.handleApiError(error, () => {
          this.$log.warn('failed to fetch indoorLevels. use fetched before.')
        })
      }
    },

    /**
     * condition の内容を反映する
     */
    $_applyCondition: function () {
      // 自身が発出できるconditionと同一の場合は反映不要
      if (this.isIndoorConditionSame) return

      const {
        indoor: { indoorSiteIds, indoorBuildingIds, indoorLevelId },
        place: { placeId },
      } = this.condition

      // 選択状態への反映
      let newSelectedId
      if (indoorLevelId) {
        newSelectedId = {
          indoorSite: indoorSiteIds?.length ? indoorSiteIds[0] : null,
          indoorBuilding: indoorBuildingIds[0], // フロアの値があるのでビルのIDもかならずある想定
          indoorLevel: indoorLevelId,
          indoorPlace: placeId,
        }
      } else {
        newSelectedId = {
          indoorSite: null,
          indoorBuilding: null,
          indoorLevel: null,
          indoorPlace: null,
        }
      }
      // 実際の反映は条件に不一致がある場合のみ
      const isAllSame = Object.keys(this.selectedId).every(
        (key) => this.selectedId[key] === newSelectedId[key]
      )
      if (!isAllSame) {
        this.selectedId = newSelectedId
      }

      // ビル選択時はリストを残すため filterCondition への反映はしない
      if (this.selectedId.indoorBuilding) {
        return
      }
      if (!this.isTargetSame) {
        // 表示対象に変更がある場合は、キーワードはクリアしてすべてサイトビル選択に反映する
        const { indoorSite: cSiteIds, indoorBuilding: cBuildingIds } =
          this.targetIdsFromCondition
        this.filterCondition = {
          keyword: null,
          indoorSiteIds: cSiteIds,
          indoorBuildingIds: cBuildingIds,
        }
      }
    },

    /**
     * 表示対象ID変更時処理
     */
    $_onTargetIdsChanged: function () {
      /**
       * 選択値変更イベント
       * @param {Object} targetIds 表示対象ID
       */
      this.$emit('target-ids-changed', this.targetIds)

      this.$_selectByTargetIds()
    },

    /**
     * targetIds の値に応じて選択する
     */
    $_selectByTargetIds: function () {
      let selectionChange = {}
      const { indoorSite, indoorBuilding, indoorLevel } = this.targetIds

      // サイト
      if (indoorSite) {
        if (indoorSite.length === 1) {
          // 1つだけの時は値を選択
          // ビルが全て自分の子の場合のみ選択可能
          const childIndoorBuildings = this.indoorSiteNodes
            .find((n) => n.id === indoorSite[0])
            .indoorBuildingNodes.map((n) => n.id)
          const containsBuildingAll =
            indoorBuilding &&
            indoorBuilding.every((id) => childIndoorBuildings.includes(id))
          if (containsBuildingAll) {
            selectionChange.indoorSite = indoorSite[0]
          }
        } else if (!indoorSite.includes(this.selectedId.indoorSite)) {
          // 選択中のIDが対象の中に無ければ選択解除
          selectionChange.indoorSite = null
        }
      }

      // ビル
      if (indoorBuilding) {
        if (indoorBuilding.length === 1) {
          // 1つだけの時は値を選択
          selectionChange.indoorBuilding = indoorBuilding[0]
        } else if (!indoorBuilding.includes(this.selectedId.indoorBuilding)) {
          // 選択中のIDが対象の中に無ければ選択解除
          selectionChange.indoorSite = null
          selectionChange.indoorBuilding = null
        }
      }

      // フロア
      if (indoorLevel) {
        if (indoorLevel.length === 1) {
          // 1つだけの時は値を選択
          selectionChange.indoorLevel = indoorLevel[0]
        } else if (!indoorLevel.includes(this.selectedId.indoorLevel)) {
          // 選択中のIDが対象の中に無ければ選択解除
          selectionChange.indoorSite = null
          selectionChange.indoorBuilding = null
          selectionChange.indoorLevel = null
        }
      }

      this.$_onItemSelected(selectionChange)
    },

    /**
     * アイテム選択時処理
     * @param {Object} data 選択情報
     */
    $_onItemSelected: function (data) {
      if (!data || !Object.keys(data).length) return

      const result = { ...this.selectedId, ...data }
      const isChanged = Object.fromEntries(
        Object.keys(this.selectedId).map((key) => [
          key,
          this.selectedId[key] !== result[key],
        ])
      )

      // 上位から下位にむけて整合性を取っていく
      // ユーザーによって変更されている場合は優先するので調整対象外

      // サイト => ビル
      if (!isChanged.indoorBuilding) {
        result.indoorBuilding = this.$_getConsistentIndoorBuildingId(
          result.indoorSite,
          result.indoorBuilding
        )
      }
      // ビル => フロア
      if (!isChanged.indoorLevel) {
        result.indoorLevel = this.$_getConsistentIndoorLevelId(
          result.indoorBuilding,
          result.indoorLevel
        )
      }
      // フロア => プレイス
      if (!isChanged.indoorPlace) {
        result.indoorPlace = this.$_getConsistentIndoorPlaceId(
          result.indoorLevel,
          result.indoorPlace
        )
      }
      // サイトなしビルの整合性
      const isSelectedParentBuilding = !!this.indoorBuildingNodesAsParent.find(
        (b) => b.id === result.indoorBuilding
      )
      if (isSelectedParentBuilding && result.indoorSite) {
        result.indoorSite = null
      }

      this.selectedId = result
    },

    /**
     * 整合性の取れたビルIDを取得する
     * @param {Number} indoorSiteId サイトID
     * @param {Number} indoorBuildingId ビルID
     */
    $_getConsistentIndoorBuildingId: function (indoorSiteId, indoorBuildingId) {
      // 親ビルに選択ビルが含まれる場合はサイトを考慮せず選択したままでよい
      const isParentBuilding = !!this.indoorBuildingNodesAsParent.find(
        (n) => n.id === indoorBuildingId
      )
      if (isParentBuilding) return indoorBuildingId

      // サイト未選択の場合もサイトを考慮せず選択したままでよい
      if (!indoorSiteId) return indoorBuildingId

      const targetSiteNode = this.indoorSiteNodes.find(
        (s) => s.id === indoorSiteId
      )
      if (!targetSiteNode) return null
      const isBuildingContains = targetSiteNode.indoorBuildingNodes.find(
        (b) => b.id === indoorBuildingId
      )

      if (isBuildingContains) {
        // 含まれているので整合性が取れている
        return indoorBuildingId
      }
      if (targetSiteNode.indoorBuildingNodes.length === 1) {
        // 含まれるビルが1つだけなら選択する
        return targetSiteNode.indoorBuildingNodes[0].id
      }
      // 選択できない
      return null
    },

    /**
     * 整合性の取れたフロアIDを取得する
     * @param {Number} indoorBuildingId ビルID
     * @param {Number} indoorLevelId フロアID
     */
    $_getConsistentIndoorLevelId: function (indoorBuildingId, indoorLevelId) {
      const targetBuildingNode = this.indoorBuildingNodes.find(
        (s) => s.id === indoorBuildingId
      )
      if (!targetBuildingNode) return null
      const isLevelContains = targetBuildingNode.indoorLevelNodes.find(
        (b) => b.id === indoorLevelId
      )

      if (isLevelContains) {
        // 含まれているので整合性が取れている
        return indoorLevelId
      }
      if (targetBuildingNode.indoorLevelNodes.length) {
        // デフォルトレベルを選択する
        return this.$_getDefaultLevelId(indoorBuildingId)
      }
      // 選択できない
      return null
    },

    /**
     * 対象ビルのデフォルトレベルIDを取得する
     * @param {Number} indoorBuildingId 対象ビルID
     * @returns {Number} デフォルトレベルID
     */
    $_getDefaultLevelId: function (indoorBuildingId) {
      const building = this.indoorBuildingMap.get(indoorBuildingId)
      if (!building) return null

      const defaultLevel =
        building.indoor_levels &&
        building.indoor_levels.find((l) => l.default_level)
      return (defaultLevel && defaultLevel.id) || null
    },

    /**
     * 整合性の取れたビルIDを取得する
     * @param {Number} indoorLevelId フロアID
     * @param {Number} indoorPlaceId プレイスID
     */
    $_getConsistentIndoorPlaceId: function (indoorLevelId, indoorPlaceId) {
      const targetLevelNode = this.indoorLevelNodes.find(
        (s) => s.id === indoorLevelId
      )
      if (!targetLevelNode) return null
      const isPlaceContains = targetLevelNode.indoorPlaceNodes.find(
        (b) => b.id === indoorPlaceId
      )

      // 含まれていればOK,含まれていない場合は選択できない
      return isPlaceContains ? indoorPlaceId : null
    },

    /**
     * 選択ID変更イベント発行
     */
    $_emitSelectedIdChangedEvent: function () {
      /**
       * 選択ID変更イベント
       * @param {Object} selectedId 選択ID
       */
      this.$emit('selected-id-changed', this.selectedId)
    },

    /**
     * 絞り込み条件変更イベント発行
     */
    $_emitConditionChangedEvent: function () {
      /**
       * 絞り込み条件変更イベント
       * @param {Object} condition 絞り込み条件(屋内分)
       */
      this.$emit('condition-changed', this.conditionToEmit)
    },

    /**
     * ローディングを表示する
     */
    $_showLoading: function () {
      this.isLoading = true
    },

    /**
     * ローディングを非表示にする
     */
    $_hideLoading: function () {
      this.isLoading = false
    },
  },
}
</script>

<style lang="scss" scoped>
@use '../../assets/scss/parts/tool-bar';
@use '../../assets/scss/parts/title-bar';

.member-indoor-item {
  width: 100%;
  height: 100%;
  display: grid;
  grid-template-rows: auto auto minmax(0, 1fr);
}

.tool-bar {
  height: auto;
  border-top-right-radius: 8px;
  background-color: #ffffff;
  overflow-y: hidden;
}

.form-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  row-gap: 8px;
}

.form-row {
  width: 100%;

  &:first-child {
    display: grid;
    grid-template-columns: 1fr max-content;
    column-gap: 8px;
  }

  .form {
    width: 100%;
  }
}

.title-bar {
  padding: 0 10px 0 16px;
  display: flex;
  justify-content: flex-start;
}

.indoor-item-list {
  width: 100%;
  height: 100%;
  padding: 0;
  margin: 0;
  list-style-type: none;
  overflow-y: auto;
  @include mixin-scrollbar;
}
</style>
